import { CdkAccordion } from '@angular/cdk/accordion';
import { DOCUMENT, NgComponentOutlet, NgTemplateOutlet } from '@angular/common';
import { afterNextRender, ChangeDetectionStrategy, Component, computed, DestroyRef, effect, ElementRef, inject, Injector, input, model, runInInjectionContext, signal, viewChild, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { commandKeymapEntries, humanizeSemanticId, useShortcuts, useShortcutScope } from '@workbuddy/ui/services/keyboard.shortcuts';
import { WorkBuddyDialogRef } from '@workbuddy/ui/v2/blocks/dialog';
import { CommandPaletteRegistration, CommandPaletteService, useCommandPalette } from '@workbuddy/ui/v2/blocks/command.palette';
import { BOARD_PANEL_HOST } from '@workbuddy/ui/v2/blocks/entity.board/components/board.panel/board.panel.host';
import { DETAIL_PANE_BACK } from '@workbuddy/ui/v2/blocks/detail.pane/detail.pane.back';
import { ENTITY_BOARD_DIALOG_OUTLET } from '@workbuddy/ui/v2/blocks/entity.board';
import { CanDeactivateEntityDialog, EntityDialogDeactivateReason, EntityDialogs, EntityDialogSource } from '@workbuddy/ui/v2/blocks/entity.dialog/entity.dialog.contracts';
import { PAGE_COMPONENTS, PageHeaderSize, PageMode } from '@workbuddy/ui/blocks/page';
import { BadgeComponent } from '@workbuddy/ui/components/badge';
import { ActionBarComponent } from '@workbuddy/ui/v2/components/action.bar';
import { ButtonIconComponent } from '@workbuddy/ui/v2/components/button';
import { VIEW_STATE_OWNER } from '@workbuddy/ui/v2/view.state';
import type { FieldMode } from '../../core/contracts/field.binding';
import type { CompiledTab } from '../../core/runtime/compiled.form';
import { readModelValue } from '../../core/runtime/model.path';
import { surfaceContentMaxWidthStyle } from '../../core/runtime/surfaces/surface.definition';
import type { SurfaceContextHandle } from '../../core/runtime/surface.context/create.surface.context.options';
import { bindSurfaceActiveTab } from '../../core/runtime/view.state/bind.surface.active.tab';
import { nextRailDock } from '../../core/rail/rail.dock';
import { railPinPreference } from '../../core/rail/rail.pin.preference';
import { WbSurfaceRailComponent } from '../../core/rail/wb.surface.rail.component';
import { WbSectionComponent } from '../../core/sections/wb.section.component';
import { resolveSurfaceActions, toSurfaceActionCommands } from '../../core/surface/surface.action.presenter';
import { resolveSurfaceHeaderKind, resolveSurfaceHeaderSlot, resolveSurfaceHeaderTag, SurfaceHeaderTag } from '../../core/surface/surface.chrome.presenter';
import { buildSectionRenderMap, groupLayoutBands, layoutSurfaceSections, visibleSectionsForPaths, type SurfaceSectionGroupedBand } from '../../core/surface/surface.section.presenter';
import type { SectionRenderInfo } from '../../core/surface/surface.section.render.info';
import { countSurfaceTabErrors, createRelationMountCache, isSurfaceTabDirty, resolveCustomTabMount, SurfaceCustomTabMount } from '../../core/surface/surface.tab.presenter';
import { WbTabShellComponent, WbTabDeclaration } from '../../core/tabs/wb.tab.shell.component';
import { SurfaceEngineManageComponent } from '../manage/surface.engine.manage.component';
import { buildDetailCommandsPalette, canOpenDetailCommands, DETAIL_COMMANDS_PALETTE_PRIORITY, detailCommandsPaletteId, detailShortcutScope, headerDetailActionCommands, resolveDetailShortcutTarget } from './detail.surface.commands';
import { DetailRailTarget, DetailSurfaceRail, DetailSurfaceRailState, resolveDetailRailTitle } from './detail.surface.rail';
import { canDeactivateDetailSurface } from './detail.surface.deactivate';
import { resolveDetailPanelControls } from './detail.surface.panel.controls';
import { surfaceCapabilityFields } from '../../core/surface/surface.capability.presenter';
import { WbSurfaceCapabilityRegionComponent } from '../../core/surface/wb.surface.capability.region.component';
import { mountDetailHostTheme } from './wb.surface.detail.host.theme';
import { askBusyLeave } from '../../core/surface/surface.busy';
import { ConfirmServicePro } from '@workbuddy/ui/blocks/confirm';

const railPin = railPinPreference('wb.detail.rail.pinned');

interface DetailTabRender {
  readonly tab: CompiledTab;
  readonly sections: readonly SectionRenderInfo[];
  readonly layout: readonly SurfaceSectionGroupedBand[];
  readonly mount: ReturnType<ReturnType<typeof createRelationMountCache>['resolve']>;
  readonly custom: SurfaceCustomTabMount | undefined;
}

@Component({
  selector: 'wb-surface-detail-host',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  imports: [CdkAccordion, NgComponentOutlet, NgTemplateOutlet, PAGE_COMPONENTS, BadgeComponent, ActionBarComponent, ButtonIconComponent, WbSectionComponent, WbSurfaceRailComponent, WbTabShellComponent, SurfaceEngineManageComponent, WbSurfaceCapabilityRegionComponent],
  templateUrl: './wb.surface.detail.host.component.html',
  styleUrls: ['./wb.surface.detail.host.component.less'],
  providers: [{ provide: DetailSurfaceRail, useExisting: WbSurfaceDetailHostComponent }],
  host: {
    '[attr.data-surface]': '"detail"',
    '[attr.data-mode]': '$mode()',
    '[attr.aria-busy]': '$busy()',
    '[class.is-board-panel]': '$inBoardPanel()',
    '[class.is-inspector]': '$inInspector()',
    '[class.is-editor]': '$editorChrome()',
    '[class.is-nested-rail]': '$nestedRail()',
    '[class.has-tabs]': '$showTabStrip()',
    '[attr.data-active-tab]': '$selectedTab() ?? null',
    // Inline so it beats the theme bridge's 800px default: a mount that passes `sectionLayout.maxWidth`
    // (null = fill) is asking to widen or drop the centered column, exactly as the Manage host honours.
    '[style.--local-detail-content-max-width]': '$contentMaxWidth()',
    '[class.is-framed]': '$frame() !== null',
    '[style.--local-detail-frame-max-width]': '$frameMaxWidth()',
    '[style.--local-detail-frame-padding]': '$framePadding()',
  },
})
export class WbSurfaceDetailHostComponent implements DetailSurfaceRail, CanDeactivateEntityDialog {

  private readonly document = inject(DOCUMENT);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly elementRef = inject(ElementRef<HTMLElement>);
  private readonly destroyRef = inject(DestroyRef);
  private readonly entityDialogs = inject(EntityDialogs, { optional: true });
  private readonly entityDialogSource = inject(EntityDialogSource, { optional: true });
  private readonly dialogRef = inject(WorkBuddyDialogRef, { optional: true });
  private readonly boardPanel = inject(BOARD_PANEL_HOST, { optional: true });
  private readonly paneBack = inject(DETAIL_PANE_BACK, { optional: true });
  private readonly parentRail = inject(DetailSurfaceRail, { optional: true, skipSelf: true });
  private readonly viewStateOwner = inject(VIEW_STATE_OWNER, { optional: true });
  private readonly commandPalette = inject(CommandPaletteService);
  private readonly confirm = inject(ConfirmServicePro);
  private readonly askBusyLeave = () => askBusyLeave(this.confirm);
  private readonly injector = inject(Injector);
  private readonly rail = new DetailSurfaceRailState();
  private readonly relationMounts = createRelationMountCache({ injector: this.injector, logScope: 'wb-surface-detail-host' });
  private readonly $nestedSurface = viewChild<SurfaceEngineManageComponent>('nestedSurface');
  private commandPaletteRegistration!: CommandPaletteRegistration;

  public readonly $handle = input.required<SurfaceContextHandle>({ alias: 'handle' });
  public readonly $target = this.rail.$target;

  protected readonly PageMode = PageMode;
  protected readonly PageHeaderSize = PageHeaderSize;
  protected readonly $railDockMode = signal(false);
  protected readonly $railPinned = model(railPin.read());
  protected readonly $railCollapsed = model(false);
  protected readonly $inBoardPanel = computed(() => this.boardPanel != null);
  /** Overlay chrome only when THIS host is the inspector guest — not a board panel nested inside one. */
  protected readonly $inInspector = computed(() => this.viewStateOwner === 'entity-inspector' && this.boardPanel == null && this.dialogRef == null && this.parentRail == null);
  protected readonly $nestedRail = computed(() => this.parentRail != null);
  protected readonly $editorChrome = computed(() => this.boardPanel != null || this.dialogRef != null);
  protected readonly $pageMode = computed(() => this.$editorChrome() ? PageMode.Editor : PageMode.Page);
  protected readonly $hideHeader = computed(() => this.boardPanel?.$ownsHeader() === true);
  /** Editor-owned header: pin is this host's chrome, driven by BOARD_PANEL_HOST. */
  protected readonly $showPanelPin = computed(() => {
    const panel = this.boardPanel;
    return panel != null && panel.$pinAvailable() && panel.$ownsHeader() !== true;
  });
  protected readonly $panelPinned = computed(() => this.boardPanel?.$pinned() === true);
  protected readonly $panelPinDisabled = computed(() => this.boardPanel?.$canPin() === false);
  protected readonly $panelPinIcon = computed(() => this.$panelPinned() ? 'fa-solid fa-thumbtack' : 'fa-regular fa-thumbtack');
  protected readonly $panelPinTooltip = computed(() => {
    if (this.$panelPinDisabled()) return 'Not enough room to pin';
    return this.$panelPinned() ? 'Unpin panel' : 'Pin panel open';
  });
  /** Stepper, record page and expand/dock beside pin, read from the same BOARD_PANEL_HOST; the board keeps the guards. */
  protected readonly $panelControls = computed(() => {
    const panel = this.boardPanel;
    return resolveDetailPanelControls(panel ? { ownsHeader: panel.$ownsHeader(), canStep: panel.$canStep(), position: panel.$stepPosition(), expand: panel.$expand(), recordPageUrl: panel.$recordPageUrl(), busy: this.$page().$busy() } : null);
  });
  protected readonly $showPanelChrome = computed(() => this.$showPanelPin() || this.$panelControls().expand != null || this.$panelControls().dock || this.$panelControls().openPage);

  /** Back to the parent record, present only while this surface is a detail pane's child. */
  protected readonly $showPaneBack = computed(() => this.paneBack != null);
  protected readonly $paneBackLabel = computed(() => this.paneBack?.$label() ?? 'Back');

  public constructor() {

    mountDetailHostTheme(this.document);
    this.commandPaletteRegistration = useCommandPalette({
      id: detailCommandsPaletteId,
      priority: DETAIL_COMMANDS_PALETTE_PRIORITY,
      enabled: () => !this.parentRail && this.canOpenCommands(),
      context: () => this.canOpenCommands() ? runInInjectionContext(this.injector, () => this.buildCommandsPalette()) : null,
    });
    this.bindCommandShortcuts();
    this.destroyRef.onDestroy(() => this.relationMounts.clear());

    effect(() => {
      railPin.write(this.$railPinned());
    });

    afterNextRender(() => {
      if (!this.parentRail && !this.boardPanel && this.entityDialogs?.$active()) this.entityDialogSource?.registerGuard(this);
      const host = this.elementRef.nativeElement;
      const syncDock = () => {
        const dock = nextRailDock(host.clientWidth, this.$railDockMode());
        if (dock !== this.$railDockMode()) this.$railDockMode.set(dock);
        if (dock && this.$railPinned() && this.$railCollapsed()) this.$railCollapsed.set(false);
      };
      syncDock();
      const observer = new ResizeObserver(() => syncDock());
      observer.observe(host);
      this.destroyRef.onDestroy(() => observer.disconnect());
    });
  }

  public async open(target: DetailRailTarget): Promise<boolean> {

    if (this.$target() && !await this.canDeactivateNestedSurface()) return false;
    await this.rail.open(target);
    this.$railCollapsed.set(false);
    return true;
  }

  public async close(): Promise<boolean> {

    if (!await this.canDeactivateNestedSurface()) return false;
    await this.rail.close();
    return true;
  }

  public async canDeactivate(_reason: EntityDialogDeactivateReason): Promise<boolean> {

    const handle = this.$handle();
    const outer = {
      $readonly: handle.page.$readonly,
      $saving: handle.page.$saving,
      $dirty: handle.page.$dirty,
      $busy: handle.page.$busy,
      flushPatch: () => handle.flushPatch(),
    };
    if (!await canDeactivateDetailSurface(outer, this.askBusyLeave)) return false;
    return this.canDeactivateNestedSurface();
  }

  protected readonly $page = computed(() => this.$handle().page);
  protected readonly $compiled = computed(() => this.$handle().compiled);
  protected readonly $model = computed(() => this.$page().$model());
  protected readonly $mode = computed(() => this.$page().$mode?.() ?? 'edit');
  protected readonly $readonly = computed(() => this.$page().$readonly());
  protected readonly $busy = computed(() => this.$page().$initializing() || this.$page().$saving());
  protected readonly $surfaceMode = computed<FieldMode>(() => {
    if (this.$mode() === 'preview' || this.$readonly()) return 'read';
    return this.$handle().surface.defaultFieldMode;
  });

  protected readonly $title = computed(() => {
    const header = this.$compiled().header;
    if (header) {
      const value = readModelValue(this.$model(), header.identifier);
      if (value != null && value !== '') return String(value);
    }
    return header?.kind ?? this.$compiled().entity ?? 'Record';
  });

  protected readonly $kind = computed(() => resolveSurfaceHeaderKind(this.$compiled().header?.kind, this.$title()));

  protected readonly $headerTags = computed<readonly SurfaceHeaderTag[]>(() => {
    const header = this.$compiled().header;
    if (!header) return [];
    const model = this.$model();
    const resolver = this.$handle().resolver;
    return header.tags.map(tag => resolveSurfaceHeaderTag(tag, model, resolver)).filter((tag): tag is SurfaceHeaderTag => !!tag);
  });

  /** Inspector already paints Close (and Back / open-page). A second X is the double chrome. */
  protected readonly $showClose = computed(() => {

    if (this.$inInspector()) return false;
    return !!this.parentRail || this.$mode() === 'create' || this.$mode() === 'edit' || !!this.entityDialogs?.$active() || !!this.dialogRef || !!this.boardPanel;
  });

  private readonly $sectionRenderInfoByPath = computed(() => buildSectionRenderMap(this.$handle()));

  protected readonly $bodySections = computed(() => visibleSectionsForPaths(this.$handle().compiled.bodySections, this.$sectionRenderInfoByPath(), this.$page()));
  protected readonly $contentMaxWidth = computed(() => surfaceContentMaxWidthStyle(this.$handle().sectionLayout.maxWidth));
  protected readonly $frame = computed(() => this.$handle().chrome.frame);
  protected readonly $frameMaxWidth = computed(() => this.$frame() ? `${this.$frame()!.maxWidth}px` : null);
  protected readonly $framePadding = computed(() => this.$frame() ? `${this.$frame()!.padding}px` : null);
  protected readonly $railSections = computed(() => visibleSectionsForPaths(this.$handle().compiled.namedSlotSections['rail'] ?? [], this.$sectionRenderInfoByPath(), this.$page()));
  protected readonly $showRail = computed(() => this.$railSections().length > 0 || this.$target() != null);
  protected readonly $railTitle = computed(() => this.$target() ? undefined : resolveDetailRailTitle(this.$railSections().map(item => item.section)));

  private readonly $resolvedCommands = computed(() => resolveSurfaceActions(this.$handle()));
  protected readonly $actionCommands = computed(() => toSurfaceActionCommands(this.$resolvedCommands(), this.$busy()));
  protected readonly $headerActionCommands = computed(() => headerDetailActionCommands(this.$actionCommands()));
  protected readonly $headerSlot = computed(() => resolveSurfaceHeaderSlot(this.$handle().resolver));
  protected readonly $capabilityFields = computed(() => surfaceCapabilityFields(this.$compiled(), this.$sectionRenderInfoByPath(), this.$page()));

  private readonly tabBinding = bindSurfaceActiveTab({
    surfaceId: 'detail',
    getPersistence: () => {
      try {
        return this.$handle().surface.viewStatePersistence ?? 'session';
      } catch {
        return 'session';
      }
    },
    getViewState: () => this.$page().viewState,
    isValidTab: id => this.$tabDeclarations().some(tab => tab.value === id && tab.visible !== false),
    defaultTab: () => this.$tabDeclarations().find(tab => tab.visible !== false)?.value,
    sessionScope: () => (this.$model() as { _id?: string } | undefined)?._id,
    isReady: () => {
      try {
        this.$handle();
        return true;
      } catch {
        return false;
      }
    },
  });

  protected readonly $selectedTab = this.tabBinding.$selectedTab;

  private readonly $detailTabs = computed<readonly DetailTabRender[]>(() => {
    const handle = this.$handle();
    const byPath = this.$sectionRenderInfoByPath();
    return handle.compiled.tabs.map(tab => {
      if (tab.type === 'relation') {
        return { tab, sections: [], layout: [], mount: this.relationMounts.resolve(tab, handle), custom: undefined };
      }
      if (tab.type === 'custom') {
        return { tab, sections: [], layout: [], mount: undefined, custom: resolveCustomTabMount(tab, handle) };
      }
      const sections = visibleSectionsForPaths(tab.sections, byPath, handle.page);
      return { tab, sections, layout: groupLayoutBands(layoutSurfaceSections(sections, handle.sectionLayout.columns, tab.sectionColumns ?? [])), mount: undefined, custom: undefined };
    });
  });

  protected readonly $tabDeclarations = computed<readonly WbTabDeclaration[]>(() => {
    const page = this.$page();
    return this.$detailTabs().map(({ tab, sections }) => {
      const tabState = page.tab(tab.path);
      return {
        value: tab.path,
        label: tabState.$title() ?? tab.title,
        visible: !tabState.$hidden(),
        errorCount: countSurfaceTabErrors(page, tab, sections),
        dirty: isSurfaceTabDirty(page, tab, sections),
      };
    });
  });

  protected readonly $showTabStrip = computed(() => this.$tabDeclarations().some(tab => tab.visible !== false));

  private readonly $tabRenderByValue = computed(() => {
    const map = new Map<string, DetailTabRender>();
    for (const info of this.$detailTabs()) map.set(info.tab.path, info);
    return map;
  });

  protected readonly $activeTabInfo = computed<DetailTabRender | undefined>(() => {
    if (!this.$showTabStrip()) return undefined;
    const byValue = this.$tabRenderByValue();
    const declarations = this.$tabDeclarations();
    const selected = this.$selectedTab();
    const active = selected && declarations.some(tab => tab.value === selected && tab.visible !== false)
      ? selected
      : declarations.find(tab => tab.visible !== false)?.value;
    return active ? byValue.get(active) : undefined;
  });

  protected readonly $bodyLayout = computed(() => {
    const info = this.$activeTabInfo();
    if (!info) return groupLayoutBands(layoutSurfaceSections(this.$bodySections(), this.$handle().sectionLayout.columns, this.$handle().compiled.sectionColumns?.['body']));
    if (info.tab.type !== 'form') return [];
    return info.layout;
  });

  protected onSelectedTabChange(value: string | undefined): void {

    this.tabBinding.selectTab(value);
  }

  private canOpenCommands(): boolean {

    try {
      const page = this.$page();
      return canOpenDetailCommands(page.$initializing(), this.$actionCommands(), (page.$model() as { _id?: unknown })._id);
    } catch {
      return false;
    }
  }

  private buildCommandsPalette() {

    if (!this.canOpenCommands()) return null;
    return buildDetailCommandsPalette(this.$title(), this.$actionCommands(), command => {
      this.commandPalette.close();
      void command.run?.();
    }, () => this.commandPalette.close());
  }

  private bindCommandShortcuts(): void {

    useShortcutScope(detailShortcutScope);
    useShortcuts(commandKeymapEntries().map(([semanticId, keys]) => ({
      id: `detail.${semanticId}`,
      keys,
      label: humanizeSemanticId(semanticId),
      scope: detailShortcutScope,
      when: () => this.canOpenCommands() && !this.commandPalette.isOpen() && !!resolveDetailShortcutTarget(semanticId, this.$actionCommands()),
      action: () => {
        const command = resolveDetailShortcutTarget(semanticId, this.$actionCommands());
        if (command) void command.run?.();
      },
    })));
  }

  protected onTogglePanelPin(): void {

    this.boardPanel?.setPinned(!this.$panelPinned());
  }

  protected onPanelStep(direction: 1 | -1): void {

    this.boardPanel?.step(direction);
  }

  protected onPanelExpand(): void {

    this.boardPanel?.expand();
  }

  protected onPanelDock(): void {

    this.boardPanel?.dock();
  }

  protected onOpenRecordPage(): void {

    this.boardPanel?.openRecordPage();
  }

  /** The pane's own leave guard runs inside `back()`, so a dirty child can still refuse to leave. */
  protected async onPaneBack(): Promise<void> {

    await this.paneBack?.back();
  }

  protected async onClose(): Promise<void> {

    if (this.parentRail) {
      await this.parentRail.close();
      return;
    }
    if (!await this.canDeactivateNestedSurface()) return;
    // An inline pane can live inside a routed creator. Its own host is the nearest close target.
    if (this.boardPanel) {
      this.boardPanel.close();
      return;
    }
    if (this.entityDialogs?.$active()) {
      await this.entityDialogs.close();
      return;
    }

    if (this.dialogRef) {
      void this.dialogRef.close();
      return;
    }

    let current: ActivatedRoute | null = this.route;
    while (current) {
      if (current.outlet === ENTITY_BOARD_DIALOG_OUTLET) {
        void this.router.navigate(
          [{ outlets: { [ENTITY_BOARD_DIALOG_OUTLET]: null } }],
          { relativeTo: current.parent ?? current, queryParamsHandling: 'preserve', replaceUrl: true },
        );
        return;
      }
      current = current.parent;
    }

    void this.router.navigateByUrl(this.router.url.replace(/\/\(dialog:[^)]+\)/, ''));
  }

  private async canDeactivateNestedSurface(): Promise<boolean> {

    const surface = this.$nestedSurface()?.$definition()?.$surface();
    return canDeactivateDetailSurface(surface, this.askBusyLeave);
  }

}
